import { ref, computed, type Ref, type ComputedRef } from 'vue';
import { staffApi, type StaffProfileDto } from '@/services/api/staffApi';
import { apiErrorMessage } from '@/utils/apiErrorTranslator';

const PROFILE_LOAD_FAILED_KEY = 'errors.action.loadProfile';

/**
 * Same shape as `useCurrentNurse` — lazy fetch of `GET /staff/me`, cached
 * for the SPA lifetime, reset on logout. Used by receptionist / billing /
 * hospital-admin screens that need position / department context the auth
 * payload doesn't carry.
 */

const profile = ref<StaffProfileDto | null>(null);
const isLoading = ref(false);
const error = ref<string | null>(null);
let inflight: Promise<void> | null = null;

async function loadOnce(): Promise<void> {
  if (profile.value) return;
  if (inflight) return inflight;

  isLoading.value = true;
  error.value = null;
  inflight = staffApi
    .getMine()
    .then((data) => {
      profile.value = data;
    })
    .catch((err: unknown) => {
      error.value = apiErrorMessage(err, PROFILE_LOAD_FAILED_KEY);
    })
    .finally(() => {
      isLoading.value = false;
      inflight = null;
    });

  return inflight;
}

export function useCurrentStaff(): {
  profile: Ref<StaffProfileDto | null>;
  isLoading: Ref<boolean>;
  error: Ref<string | null>;
  position: ComputedRef<string>;
  department: ComputedRef<string>;
  refresh: () => Promise<void>;
} {
  void loadOnce();

  const position = computed(() => profile.value?.position ?? '');
  const department = computed(() => profile.value?.department ?? '');

  const refresh = async () => {
    profile.value = null;
    inflight = null;
    await loadOnce();
  };

  return { profile, isLoading, error, position, department, refresh };
}

/** Clear the cached staff profile — call from the auth store on logout. */
export function resetCurrentStaff(): void {
  profile.value = null;
  isLoading.value = false;
  error.value = null;
  inflight = null;
}
